
import React from 'react';
import { Stack } from 'office-ui-fabric-react/lib/Stack';
import { mergeStyleSets } from 'office-ui-fabric-react/lib/Styling';
import { useDispatch, useSelector  } from 'react-redux';
import Feature from 'ol/Feature';

import { Log, PanelTitle } from '../../core';
import { getSearchProjectId } from '../../project';
import { getTempFeature, sagas as sagasMap } from '../../map';

import useSearchDefinition from '../hooks/useSearchDefinition';
import { getUi } from '../reducers'
import { getIsGeoActive, getSearchData } from '../reducers/ui';
import { getActions } from '../sagas';
import SearchForm from './SearchForm';
import SearchResult from './SearchResult';

const log = Log.getLogger('MultiSceneForApp.search.components.SearchPanell');

const SearchPanel = (props) => {

    const classNames = mergeStyleSets({
        SearchPanel: {
            boxSizing: 'border-box',
            height: '100%',
            minHeight: 0,
            overflow: 'hidden',
            padding: 4
        },
        header: {
            flexShrink: 0
        },
        form: {
            maxHeight: '60%',
            minHeight: 0,
            overflow: 'hidden'
        },
        results: {
            minHeight: 0,
            overflow: 'hidden'
        }
    });

    const dispatch = useDispatch();
    const searchActions = getActions(dispatch);
    const mapActions = sagasMap.getActions(dispatch);

    const searchProjectId = useSelector(state => getSearchProjectId(state.project));
    const definition = useSearchDefinition();
    const searchData = useSelector(state => getSearchData(getUi(state.search)));
    const isGeoActive = useSelector(state => getIsGeoActive(getUi(state.search)));
    const tempFeature = useSelector(state => getTempFeature(state.map.map));

    React.useEffect(() => {
        if (tempFeature) {
            mapActions.addCommand('SET_TEMP_FEATURE', {features: [tempFeature]});
        }
    }, []);

    React.useEffect(() => {
        if (isGeoActive) {
            mapActions.setToolsMode('INPUT_POLYGON');
        }
    }, [isGeoActive]);

    const handleDoSearch = (queryId, operation) => {
        if (searchProjectId) {
            searchActions.doSearch(searchProjectId, queryId, searchData, '', false, operation);
        } else {
            log.warn(`ProjectId for search is empty. Check project definition for "app.modules.search.projectId"`);
        }
    }

    const handleSelectQuery = (queryId, queryType) => {
        searchActions.selectQuery(queryId, queryType);
        mapActions.setTempFeature(null);
        mapActions.addCommand('SET_TEMP_FEATURE', {features: []});
    }

    const handleSetAttrCondition = (attrId, condition) => {
        searchActions.setAttrCondition(attrId, condition);
    }

    const handleSetGeomCondition = (geometry, condition) => {
        searchActions.setGeomCondition(geometry, condition);
    }

    const handleSetGeometry = (geometry, zoomToGeometry) => {
        let feature = (geometry)
            ? new Feature({geometry: geometry})
            : null;

        mapActions.addCommand('SET_TEMP_FEATURE', {features: feature ? [feature] : []});
        mapActions.setTempFeature(feature);
        if (zoomToGeometry) {
            mapActions.addCommand('ZOOM_FEATURE', {geometry: geometry});
        }
    }

    const handleSetIsGeoActive = (active) => {
        mapActions.setToolsMode(
            active ? 'INPUT_POLYGON' : 'DEFAULT'
        );
        searchActions.setIsGeoActive(active);
    }

    const handleSetIsValid = (isValid) => {
        searchActions.setIsValid(isValid);
    }

    const handleSetParamValue = (paramId, value) => {
        searchActions.setParamValue(paramId, value);
    }

    return (
        <Stack verticalFill className={classNames.SearchPanel} tokens={{childrenGap: 4}}>
            <Stack.Item align="stretch" className={classNames.header}>
                <PanelTitle title="VYHLEDÁVÁNÍ" />
            </Stack.Item>
            <Stack.Item shrink={1} align="stretch" className={classNames.form}>
                {
                    definition.searchDefinition &&
                    <SearchForm
                        definition={definition}
                        onDoSearch={handleDoSearch}
                        onSelectQuery={handleSelectQuery}
                        onSetAttrCondition={handleSetAttrCondition}
                        onSetGeometry={handleSetGeometry}
                        onSetGeomCondition={handleSetGeomCondition}
                        onSetIsGeoActive={handleSetIsGeoActive}
                        onSetIsValid={handleSetIsValid}
                        onSetParamValue={handleSetParamValue}
                    />
                }
            </Stack.Item>
            <Stack.Item align="stretch">
                {
                    !definition.searchDefinition &&
                    <div>Nahrávám...</div>
                }
            </Stack.Item>
            <Stack.Item grow={1} align="stretch" className={classNames.results}>
                <SearchResult
                    appActions={props.appActions}
                    onObjectInfo={props.onObjectInfo}
                />
            </Stack.Item>
        </Stack>
    );
}

export default SearchPanel;
